/**
 * Document Text Extraction Utilities
 * 
 * This module provides utilities for extracting plain text content from
 * various document formats including .txt, .docx, and .rtf files.
 * 
 * Supported formats:
 * - .txt: Plain text files (existing implementation)
 * - .docx: Microsoft Word documents (via mammoth.js)
 * - .rtf: Rich Text Format documents (via rtf.js)
 * 
 * Author: Enhanced Segmentation Agent v2.0
 * Date: July 8, 2025
 */

import mammoth from 'mammoth'
import { API_ENDPOINTS } from '../config/api'

const DOCUMENT_EXTRACTION_TIMEOUT_MS = 45000

/**
 * Extract plain text content from a file using the Python backend
 * @param file - The file to extract text from
 * @returns Promise that resolves to the extracted text content
 */
export interface DocumentExtractionOptions {
  preserveEditorialNotes?: boolean
}

export async function extractTextContent(
  file: File,
  options: DocumentExtractionOptions = {},
): Promise<string> {
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
  
  // For .txt files, we can still handle them client-side for speed
  if (extension === '.txt') {
    return await extractTextFromTxt(file)
  }
  
  // For .docx and .rtf files, use the Python backend
  return await extractTextFromBackend(file, options)
}

/**
 * Extract text content using the Python backend API
 * @param file - The file to extract text from
 * @returns Promise that resolves to the extracted text content
 */
async function extractTextFromBackend(
  file: File,
  options: DocumentExtractionOptions,
): Promise<string> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  try {
    const formData = new FormData()
    formData.append('file', file)

    const timeoutPromise = new Promise<Response>((_, reject) => {
      timeoutId = setTimeout(() => {
        controller?.abort()
        reject(new Error(`Document extraction timed out after ${Math.round(DOCUMENT_EXTRACTION_TIMEOUT_MS / 1000)} seconds.`))
      }, DOCUMENT_EXTRACTION_TIMEOUT_MS)
    })
    const endpoint = options.preserveEditorialNotes
      ? `${API_ENDPOINTS.extractDocument}?preserve_editorial_notes=true`
      : API_ENDPOINTS.extractDocument
    const response = await Promise.race([fetch(endpoint, {
      method: 'POST',
      body: formData,
      ...(controller ? { signal: controller.signal } : {}),
    }), timeoutPromise])
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.detail || `Server error: ${response.status}`)
    }
    
    const result = await response.json()
    return result.text
    
  } catch (error) {
    console.error('Error extracting text from backend:', error)
    throw new Error(`Failed to extract text from ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * Extract text from .txt files (existing implementation)
 */
async function extractTextFromTxt(file: File): Promise<string> {
  return await file.text()
}

/**
 * Extract text from .docx files using mammoth.js
 */
async function extractTextFromDocx(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const result = await mammoth.extractRawText({ arrayBuffer })
    
    if (result.messages && result.messages.length > 0) {
      console.warn('Mammoth parsing warnings:', result.messages)
    }
    
    return result.value.trim()
  } catch (error) {
    console.error('Error extracting text from .docx file:', error)
    throw new Error(`Failed to extract text from .docx file: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Extract text from .rtf files using improved RTF parsing
 * This implementation is specifically designed to handle Nisus Writer Pro and other RTF formats
 */
async function extractTextFromRtf(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const rtfData = new Uint8Array(arrayBuffer)
    
    // Convert to string since RTF is text-based
    const rtfString = new TextDecoder('utf-8').decode(rtfData)
    
    // Advanced RTF text extraction optimized for Nisus Writer Pro
    const textContent = parseRtfContent(rtfString)
    
    if (!textContent || textContent.trim().length < 3) {
      throw new Error('No readable text content found in RTF file')
    }
    
    return textContent.trim()
  } catch (error) {
    console.error('Error extracting text from .rtf file:', error)
    throw new Error(`Failed to extract text from .rtf file: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Advanced RTF content parser with balanced brace handling
 * Designed to handle complex RTF structures from Nisus Writer Pro and other RTF editors
 */
function parseRtfContent(rtfString: string): string {
  try {
    let content = rtfString
    
    // Step 1: Remove RTF header completely
    content = content.replace(/^\\rtf\d+[^{]*/, '')
    
    // Step 2: Remove balanced brace structures using proper parsing
    content = removeBalancedBraceStructures(content, [
      'fonttbl',
      'colortbl', 
      'stylesheet',
      'info',
      'listtable',
      'listoverridetable',
      'filetbl',
      'revtbl',
      'generator'
    ])
    
    // Step 3: Remove Nisus Writer Pro specific headers and metadata
    content = content.replace(/\\nisus[a-zA-Z]+[^\\]*/g, '')
    content = content.replace(/\\cocoartf\d+/g, '')
    content = content.replace(/\\ansicpg\d+/g, '')
    content = content.replace(/\\mac\b/g, '')
    
    // Step 4: Remove document setup and formatting
    content = content.replace(/\\deff\d+/g, '')
    content = content.replace(/\\paperw\d+/g, '')
    content = content.replace(/\\paperh\d+/g, '')
    content = content.replace(/\\margl\d+/g, '')
    content = content.replace(/\\margr\d+/g, '')
    content = content.replace(/\\margt\d+/g, '')
    content = content.replace(/\\margb\d+/g, '')
    content = content.replace(/\\vieww\d+/g, '')
    content = content.replace(/\\viewh\d+/g, '')
    content = content.replace(/\\viewkind\d+/g, '')
    content = content.replace(/\\viewscale\d+/g, '')
    
    // Step 5: Remove paragraph and character formatting
    content = content.replace(/\\pard[^\\{]*(?=\\|\{|$)/g, '')
    content = content.replace(/\\tx\d+/g, '')
    content = content.replace(/\\q[lrcj]/g, '')
    content = content.replace(/\\qnatural/g, '')
    content = content.replace(/\\pardirnatural/g, '')
    content = content.replace(/\\pardirrtl/g, '')
    content = content.replace(/\\plain/g, '')
    
    // Step 6: Remove font references and character formatting
    content = content.replace(/\\f\d+/g, '')
    content = content.replace(/\\fs\d+/g, '')
    content = content.replace(/\\cf\d+/g, '')
    content = content.replace(/\\b\d*/g, '')
    content = content.replace(/\\i\d*/g, '')
    content = content.replace(/\\ul/g, '')
    content = content.replace(/\\ulnone/g, '')
    content = content.replace(/\\strike/g, '')
    content = content.replace(/\\striked\d*/g, '')
    
    // Step 7: Handle Unicode characters before other processing
    content = content.replace(/\\uc\d+\\u(\d+)\s?/g, function(match, unicode) {
      const codePoint = parseInt(unicode)
      if (codePoint > 0 && codePoint <= 0x10FFFF) {
        return String.fromCharCode(codePoint)
      }
      return ''
    })
    
    // Step 8: Remove all remaining RTF control words and sequences
    content = content.replace(/\\[a-zA-Z]+\d*\s?/g, ' ')
    content = content.replace(/\\[^a-zA-Z\s]/g, '')
    
    // Step 9: Handle RTF escape sequences
    content = content.replace(/\\'/g, "'")
    content = content.replace(/\\\\/g, '\\')
    content = content.replace(/\\{/g, '{')
    content = content.replace(/\\}/g, '}')
    content = content.replace(/\\~/g, ' ')
    content = content.replace(/\\-/g, '-')
    content = content.replace(/\\_/g, '_')
    
    // Step 10: Remove all braces (after handling escape sequences)
    content = content.replace(/[{}]/g, '')
    
    // Step 11: Clean up version numbers and artifact patterns
    content = content.replace(/\b\d+\.\d+\.\d+\b/g, '') // Version numbers like 15.5.0
    content = content.replace(/\bStandardindex\b/g, '')
    content = content.replace(/\bIndex\s+\d+\b/g, '')
    content = content.replace(/\b[Üü]berschrift\b/g, '') // German "Überschrift" (heading)
    content = content.replace(/\?berschrift\b/g, '') // Malformed encoding
    
    // Step 12: Remove font name artifacts and common RTF remnants
    content = content.replace(/\b(GeezaPro|Geeza\s+Pro|Tahoma|Tahoma-Bold|GentiumPlus|Gentium\s+Plus|Standard-IHV|IHV\s+\d+)\b/g, '')
    content = content.replace(/\b(Times-Roman|Helvetica|Arial|Courier|Symbol)\b/g, '')
    content = content.replace(/\b(Normal|Heading\s+\d+|Title|Subtitle)\b/g, '')
    
    // Step 13: Remove semicolon artifacts and excessive punctuation
    content = content.replace(/;+/g, '')
    content = content.replace(/\s*-\s*Bold\s*/g, '')
    content = content.replace(/\s*-\s*Italic\s*/g, '')
    content = content.replace(/\s*-\s*Regular\s*/g, '')
    
    // Step 14: Final cleanup - remove extra whitespace and normalize
    content = content.replace(/\s+/g, ' ')
    content = content.replace(/\n\s*\n/g, '\n')
    content = content.replace(/^\s+|\s+$/g, '')
    
    // Step 15: Remove any remaining backslashes and control characters
    content = content.replace(/\\+/g, ' ')
    // eslint-disable-next-line no-control-regex
    content = content.replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
    
    // Step 16: Final whitespace normalization
    content = content.replace(/\s+/g, ' ')
    content = content.trim()
    
    return content
  } catch (error) {
    console.error('Error parsing RTF content:', error)
    return ''
  }
}

/**
 * Helper function to remove balanced brace structures from RTF content
 */
function removeBalancedBraceStructures(content: string, structures: string[]): string {
  for (const structure of structures) {
    const pattern = `\\{\\\\${structure}`
    let startIndex = content.indexOf(pattern)
    
    while (startIndex !== -1) {
      let braceCount = 0
      let i = startIndex
      let endIndex = -1
      
      // Find the matching closing brace
      while (i < content.length) {
        const char = content[i]
        if (char === '{') {
          braceCount++
        } else if (char === '}') {
          braceCount--
          if (braceCount === 0) {
            endIndex = i
            break
          }
        }
        i++
      }
      
      if (endIndex !== -1) {
        // Remove the entire structure
        content = content.substring(0, startIndex) + content.substring(endIndex + 1)
        // Look for the next occurrence
        startIndex = content.indexOf(pattern, startIndex)
      } else {
        // If we can't find a matching brace, just remove the opening pattern
        content = content.replace(pattern, '')
        startIndex = content.indexOf(pattern)
      }
    }
  }
  
  return content
}

/**
 * Check if a file type is supported
 */
export function isSupportedFileType(fileName: string): boolean {
  const supportedExtensions = ['.txt', '.docx', '.rtf']
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
  return supportedExtensions.includes(extension)
}

/**
 * Get the list of supported file extensions
 */
export function getSupportedExtensions(): string[] {
  return ['.txt', '.docx', '.rtf']
}

/**
 * Get the MIME types for supported file formats
 */
export function getSupportedMimeTypes(): string[] {
  return [
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/rtf',
    'text/rtf'
  ]
}

/**
 * Get file type description for UI display
 */
export function getFileTypeDescription(extension: string): string {
  switch (extension.toLowerCase()) {
    case '.txt':
      return 'Plain Text'
    case '.docx':
      return 'Microsoft Word Document'
    case '.rtf':
      return 'Rich Text Format'
    default:
      return 'Unknown'
  }
}
